import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSidebarPermissions } from '../contexts/SidebarPermissionsContext';
import { clientProductsFor, ROLE_LABELS } from '../lib/accessProfiles';
import { 
  Home, 
  User, 
  Users,
  Settings, 
  Menu,
  X,
  ChevronDown,
  Building2
} from 'lucide-react';

const wordmark = '/brand/logos/Logo_Kakau_Tech_-04.png';

function Sidebar() {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const { user } = useAuth();
  const { hasPermission, loading: permissionsLoading } = useSidebarPermissions();
  const navigate = useNavigate();
  const location = useLocation();

  const toggleSidebar = () => setIsOpen(!isOpen);
  const closeSidebar = () => setIsOpen(false);

  const toggleExpanded = (itemKey) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemKey]: !prev[itemKey]
    }));
  };

  const navigationItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: Home,
      path: '/dashboard',
      color: 'text-[#7ec8e3]'
    },
    {
      key: 'profile',
      label: 'Meu Perfil',
      icon: User,
      path: '/profile',
      color: 'text-[#7ec8e3]'
    },
    {
      key: 'clients',
      label: 'Clientes',
      icon: Users,
      path: '/clients',
      color: 'text-[#7ec8e3]',
      subItems: [
        {
          key: 'guaibim',
          label: 'Guaibim',
          icon: Building2,
          color: 'text-[#7ec8e3]',
          subItems: clientProductsFor(user?.role).map((product) => ({
            key: product.key,
            label: product.label,
            subItems: product.links
          }))
        }
      ]
    },
    {
      key: 'settings',
      label: 'Configurações',
      icon: Settings,
      path: '/settings',
      color: 'text-[#7ec8e3]',
      subItems: [
        { label: 'Gerenciar Usuários', path: '/users' },
        { label: 'Configurações Organizacionais', path: '/settings/organizational' },
        { label: 'Configurações de API', path: '/settings/api-configs' },
        { label: 'Permissões da Sidebar', path: '/settings/sidebar-permissions' },
        { label: 'Entrada de e-mail', path: '/settings/email-intake' }
      ]
    }
  ];

  const isActive = (path) => {
    if (!path) return false;
    const [pathname, search = ''] = path.split('?');
    if (location.pathname !== pathname) return false;
    if (!search) return true;
    const expected = new URLSearchParams(search);
    const current = new URLSearchParams(location.search);
    return Array.from(expected.entries()).every(([key, value]) => current.get(key) === value);
  };
  const isSubItemActive = (subItems) => {
    return subItems?.some(subItem => (
      isActive(subItem.path) || isSubItemActive(subItem.subItems)
    ));
  };

  const renderNavItem = (item) => {
    // Verificar se o usuário tem acesso a este item usando permissões dinâmicas
    if (!hasPermission(item.key)) {
      return null;
    }

    const Icon = item.icon;
    const visibleSubItems = (item.subItems || []).filter((subItem) => (
      (!subItem.roles || subItem.roles.includes(user?.role))
      && (!subItem.subItems || subItem.subItems.length > 0)
    ));
    const hasSubItems = visibleSubItems.length > 0;
    const isExpanded = expandedItems[item.key];
    const isItemActive = isActive(item.path) || isSubItemActive(item.subItems);

    return (
      <div key={item.key}>
        <button
          onClick={() => {
            if (hasSubItems) {
              toggleExpanded(item.key);
            } else {
              navigate(item.path);
              closeSidebar();
            }
          }}
          className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200 hover:bg-white/10 ${
            isItemActive 
              ? 'bg-white/10 text-white border-r-2 border-primary-600' 
              : 'text-[#e2e8f0]'
          }`}
        >
          <div className="flex items-center space-x-3">
            <Icon className={`h-5 w-5 ${item.color}`} />
            <span>{item.label}</span>
          </div>
          {hasSubItems && (
            <ChevronDown 
              className={`h-4 w-4 text-[#94a3b8] transition-transform duration-200 ${
                isExpanded ? 'rotate-180' : ''
              }`}
            />
          )}
        </button>

        {/* Sub-items */}
        {hasSubItems && isExpanded && (
          <div className="ml-8 mt-2 space-y-1">
            {visibleSubItems
              .map((subItem, index) => {
                // Verificar se o subItem tem seus próprios subItems (nível 3)
                const hasSubSubItems = subItem.subItems && subItem.subItems.length > 0;
                const isSubItemExpanded = expandedItems[subItem.key];
                
                return (
                  <div key={index}>
                    <button
                      onClick={() => {
                        if (hasSubSubItems) {
                          toggleExpanded(subItem.key);
                        } else if (subItem.path) {
                          navigate(subItem.path);
                          closeSidebar();
                        }
                      }}
                      className={`w-full flex items-center justify-between px-4 py-2 text-sm rounded-md transition-colors duration-200 hover:bg-white/10 ${
                        isActive(subItem.path) || (hasSubSubItems && isSubItemActive(subItem.subItems))
                          ? 'text-white bg-white/10'
                          : 'text-[#cbd5e1]'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        {subItem.icon && <subItem.icon className={`h-4 w-4 ${subItem.color}`} />}
                        <span>{subItem.label}</span>
                      </div>
                      {hasSubSubItems && (
                        <ChevronDown 
                          className={`h-3 w-3 text-[#94a3b8] transition-transform duration-200 ${
                            isSubItemExpanded ? 'rotate-180' : ''
                          }`}
                        />
                      )}
                    </button>

                    {/* Products under the client, and their links one level deeper */}
                    {hasSubSubItems && isSubItemExpanded && (
                      <div className="ml-4 mt-1 space-y-1">
                        {subItem.subItems
                          .filter(subSubItem => !subSubItem.roles || subSubItem.roles.includes(user?.role))
                          .map((subSubItem, subIndex) => {
                            const hasLinks = subSubItem.subItems && subSubItem.subItems.length > 0;
                            const linksExpanded = expandedItems[subSubItem.key];
                            return (
                              <div key={subSubItem.key || subIndex}>
                                <button
                                  onClick={() => {
                                    if (hasLinks) {
                                      toggleExpanded(subSubItem.key);
                                    } else if (subSubItem.path) {
                                      navigate(subSubItem.path);
                                      closeSidebar();
                                    }
                                  }}
                                  className={`w-full flex items-center justify-between px-4 py-2 text-sm rounded-md transition-colors duration-200 hover:bg-white/10 ${
                                    isActive(subSubItem.path) || (hasLinks && isSubItemActive(subSubItem.subItems))
                                      ? 'text-white bg-white/10'
                                      : 'text-[#cbd5e1]'
                                  }`}
                                >
                                  <span>{subSubItem.label}</span>
                                  {hasLinks && (
                                    <ChevronDown
                                      className={`h-3 w-3 text-[#94a3b8] transition-transform duration-200 ${
                                        linksExpanded ? 'rotate-180' : ''
                                      }`}
                                    />
                                  )}
                                </button>
                                {hasLinks && linksExpanded && (
                                  <div className="ml-4 mt-1 space-y-1">
                                    {subSubItem.subItems
                                      .filter(link => !link.roles || link.roles.includes(user?.role))
                                      .map((link, linkIndex) => (
                                        <button
                                          key={link.path || linkIndex}
                                          onClick={() => {
                                            navigate(link.path);
                                            closeSidebar();
                                          }}
                                          className={`w-full text-left px-4 py-2 text-sm rounded-md transition-colors duration-200 hover:bg-white/10 ${
                                            isActive(link.path)
                                              ? 'text-white bg-white/10'
                                              : 'text-[#cbd5e1]'
                                          }`}
                                        >
                                          {link.label}
                                        </button>
                                      ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={toggleSidebar}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-md bg-[#0d1b2a] text-white shadow-lg border border-white/10"
      >
        {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>

      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-[260px] min-w-[260px] flex flex-col text-[#e2e8f0]
        bg-gradient-to-b from-[#0d1b2a] to-[#152238] border-r border-white/10
        transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Header */}
        <div className="flex items-center justify-center p-6 border-b border-white/10 relative">
          <div className="flex items-center justify-center">
            <img 
              src={wordmark} 
              alt="Kakau Tech" 
              className="h-12 w-auto object-contain"
            />
          </div>
          <button
            onClick={closeSidebar}
            className="lg:hidden absolute right-4 p-1 rounded-md text-[#94a3b8] hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
              <User className="h-5 w-5 text-[#e2e8f0]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {user?.fullName || user?.username}
              </p>
              <p className="text-xs text-[#94a3b8]">
                {ROLE_LABELS[user?.role] || 'Usuário'}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {permissionsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-400"></div>
            </div>
          ) : (
            navigationItems.map(renderNavItem)
          )}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 mt-auto">
          <div className="text-center">
            <img 
              src={wordmark} 
              alt="Kakau Tech" 
              className="h-8 w-auto mx-auto object-contain opacity-70"
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default Sidebar;
